import React, { useState } from "react";
import { Create } from "react-admin";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
  Select,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { CouponType } from "../../enums/CouponType";
import { ProductType } from "../../enums/ProductType";
import { DateTimePicker } from "@mui/x-date-pickers";
import { CouponServiceV2 } from "../../services/CuponServiceV2";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import AddDateInclusiveExclusiveInCoupon from "../Common/AddDateInclusiveExclusiveInCoupon";
import CouponAppPreview from "./CouponAppPreview";
import { TimeEnum } from "../../enums/TimeEnum";
import { formatDate } from "../../utils/DateTimeUtils";
import {
  WorkspaceContainer,
  WorkspacePanel,
  WorkspaceHeader,
  WorkspaceHeaderMeta,
  HeaderMetaGroup,
  HeaderMetaLabel,
  WorkspaceSection,
  SectionTitle,
  SectionTitleRow,
  WorkSpaceTitleRow,
  WorkspaceIconBadge,
  WorkspaceTitle,
  WorkspaceSubtitle,
  FormGrid,
  FormFieldGroup,
  FormLabel,
  ThreeColGrid,
  StatusToggleSwitch,
  HorizontalLine,
} from "../../styles/commonStyles";

const couponServiceV2 = new CouponServiceV2();

const categoryItems = [
  { category: "Sales", subCategory: ["Sales"] },
  { category: "Marketing", subCategory: ["Offline", "Online"] },
  { category: "Retention", subCategory: ["Winback", "Renewal", "Previous Bundle Credit"] },
];

// ── Shared input sx ───────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    padding: 0,
    border: "1px solid rgba(108, 124, 147, 0.2)",
    borderRadius: "6px",
    backgroundColor: "rgb(249, 250, 251)",
    fontSize: "14px",
    transition: "all 0.2s ease",
    "& fieldset": { display: "none" },
    "&:hover": { backgroundColor: "#F1F3F5" },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      borderColor: "#1976d2",
      boxShadow: "0 0 0 3px rgba(25,118,210,0.1)",
    },
  },
  "& .MuiInputBase-input": {
    padding: "10px 14px",
    fontSize: "14px",
  },
  "& .MuiInputAdornment-positionStart": {
    marginLeft: "12px",
    marginRight: "2px",
  },
  "& .MuiInputAdornment-positionEnd": {
    marginRight: "12px",
    marginLeft: "2px",
  },
};

const selectSx = {
  border: "1px solid rgba(108, 124, 147, 0.2)",
  borderRadius: "6px",
  backgroundColor: "rgb(249, 250, 251)",
  fontSize: "14px",
  "& .MuiSelect-select": { padding: "10px 14px", fontSize: "14px" },
  "& fieldset": { display: "none" },
  "&:hover": { backgroundColor: "#F1F3F5" },
  "&.Mui-focused": { backgroundColor: "#fff", boxShadow: "0 0 0 3px rgba(25,118,210,0.1)" },
};

const accSx = {
  boxShadow: "none",
  border: "1px solid rgba(108, 124, 147, 0.2)",
  borderRadius: "10px !important",
  overflow: "hidden",
  "&:before": { display: "none" },
  "&.Mui-expanded": { mt: 0, borderColor: "text.secondary" },
  transition: "border-color 0.2s",
};

// ── Component ─────────────────────────────────────────────────────────────────

const CouponCreateV2 = () => {
  const [couponCode, setCouponCode] = useState<string>("");
  const [couponValue, setCouponValue] = useState<string>("");
  const [couponType, setCouponType] = useState<string>("");
  const [productType, setProductType] = useState<string>("");
  const [usageLimitPerCustomer, setUsageLimitPerCustomer] = useState<Number>(0);
  const [maximumUsageLimit, setMaximumUsageLimit] = useState<Number>(0);
  const [minimumOrderValue, setMinimumOrderValue] = useState<string>("");
  const [maximumDiscountAmount, setMaximumDiscountAmount] = useState<string>("");
  const [minimumTotalAfterDiscount, setMinimumTotalAfterDiscount] = useState<string>("");
  const [couponHeader, setCouponHeader] = useState<string>("");
  const [couponDescription, setCouponDescription] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [visibleOnApp, setVisibleOnApp] = useState<boolean>(true);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [reasonSubCategory, setReasonSubCategory] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [userSet, setUserSet] = useState<any>();
  const [areaSet, setAreaSet] = useState<any>();
  const [termsAndConditions, setTermsAndConditions] = useState<string>("");
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [disableAreaSetButton, setDisableAreaSetButton] = useState<boolean>(false);
  const [couponDisplayString, setCouponDisplayString] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [dateInclusiveExclusive, setDateInclusiveExclusive] = useState<boolean>(false);
  const [inclusiveData, setInclusiveData] = useState<any>(null);
  const [userInclusionExclusion, setUserInclusionExclusion] = useState<boolean>(false);
  const [disableUserInclusionExclusion, setDisableUserInclusionExclusion] = useState<boolean>(false);
  const [userInclusionExclusionData, setUserInclusionExclusionData] = useState<any>(null);
  const [timeInclusionExclusion, setTimeInclusionExclusion] = useState<boolean>(false);
  const [addTimeInclusion, setAddTimeInclusion] = useState<boolean>(false);
  const [selectedTimeEnums, setSelectedTimeEnums] = useState<any>([]);
  const [isCashbackEnabled, setIsCashbackEnabled] = useState<boolean>(false);
  const [cashbackType, setCashbackType] = useState<string>("");
  const [cashbackValue, setCashbackValue] = useState<string>("");
  const [cashbackExpiryDays, setCashbackExpiryDays] = useState<string>("");
  const navigate = useNavigate();

  const getTimeData = () => {
    if (selectedTimeEnums.length > 0) {
      return { type: addTimeInclusion ? "inclusion" : "exclusion", timeSlots: selectedTimeEnums };
    }
    return null;
  };

  const validate = () => {
    const errors: any = {};
    if (Number(couponValue) < 0) errors.couponValue = "Must be greater than 0";
    if (couponType) errors.couponType = "Required";
    if (productType) errors.productType = "Required";
    return errors;
  };

  const handleSubmit = () => {
    if (validate()) {
      const payload = {
        couponCode: couponCode.trim(),
        couponValue,
        couponType,
        productType,
        usageLimitPerCustomer: Number(usageLimitPerCustomer),
        maximumUsageLimit: Number(maximumUsageLimit),
        minimumOrderValue,
        maximumDiscountAmount,
        couponHeader,
        couponDescription,
        reason: selectedCategory + " " + reasonSubCategory,
        isActive,
        visibleOnApp,
        expiresAt: moment(expiryDate).format("YYYY-MM-DD HH:mm:ss"),
        startsAt: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
        userSet: userSet ? userSet.trim().split("\n").map((x: any) => Number(x)).filter((x: any) => !isNaN(x)) : userSet,
        areaSet: areaSet ? areaSet.trim().split("\n").map((x: any) => Number(x)).filter((x: any) => !isNaN(x)) : areaSet,
        minimumTotalAfterDiscount: minimumTotalAfterDiscount ? Number(minimumTotalAfterDiscount) : null,
        couponDisplayString: couponDisplayString ? couponDisplayString.trim() : "",
        termsAndConditions: termsAndConditions ? termsAndConditions.trim().split(",").map((t) => t.trim()).filter(Boolean) : [],
        errorMessages: error || "",
        dateInclusionExclusionRule: inclusiveData || null,
        userInclusionExclusionRule: userInclusionExclusion && userInclusionExclusionData ? userInclusionExclusionData : null,
        timeInclusionExclusionRule: timeInclusionExclusion ? getTimeData() : null,
        isCashbackEnabled,
        cashbackType: isCashbackEnabled ? cashbackType : null,
        cashbackValue: isCashbackEnabled ? cashbackValue : null,
        cashbackExpiryDays: isCashbackEnabled && cashbackExpiryDays ? Number(cashbackExpiryDays) : null,
      };
      couponServiceV2.createCouponV2(payload).then((data) => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Success", type: CustomSnackbarTypes.SUCCESS });
        if (data?.data.id) navigate(`/CouponsV2/${data?.data?.id}/show`, { replace: true });
        setCouponCode(""); setCouponValue(""); setCouponType(""); setProductType("");
        setUsageLimitPerCustomer(0); setMaximumUsageLimit(0);
        setMinimumOrderValue(""); setMaximumDiscountAmount("");
        setCouponHeader(""); setCouponDescription("");
        setSelectedCategory(""); setReasonSubCategory("");
        setUserSet(""); setAreaSet("");
        setStartDate(null); setExpiryDate(null);
        setIsActive(true); setVisibleOnApp(true);
        setUserInclusionExclusion(false); setDisableUserInclusionExclusion(false);
        setUserInclusionExclusionData(null);
        setDateInclusiveExclusive(false); setInclusiveData(null);
        setMinimumTotalAfterDiscount(""); setCouponDisplayString("");
        setTermsAndConditions(""); setError("");
        setIsCashbackEnabled(false); setCashbackType(""); setCashbackValue(""); setCashbackExpiryDays("");
      });
    }
  };

  // Accordion summaries
  const userCount = userSet ? userSet.trim().split("\n").filter((x: string) => x.trim() && !isNaN(Number(x))).length : 0;
  const areaCount = areaSet ? areaSet.trim().split("\n").filter((x: string) => x.trim() && !isNaN(Number(x))).length : 0;
  const targetingLabel = userCount > 0 || areaCount > 0
    ? [userCount > 0 && `${userCount} users`, areaCount > 0 && `${areaCount} areas`].filter(Boolean).join(", ")
    : "Not set";
  const rulesConfigured = [inclusiveData && "date", disableUserInclusionExclusion && "user", timeInclusionExclusion && "time"].filter(Boolean);
  const rulesLabel = rulesConfigured.length > 0 ? rulesConfigured.join(", ") : "None";
  const cashbackLabel = isCashbackEnabled
    ? `${cashbackType || "—"} · ${cashbackValue || "—"}${cashbackType === "PERCENT" ? "%" : " ₹"}${cashbackExpiryDays ? ` · ${cashbackExpiryDays}d` : ""}`
    : "Disabled";
  const termCount = termsAndConditions ? termsAndConditions.split(",").map((t) => t.trim()).filter(Boolean).length : 0;
  const termsLabel = [termCount > 0 && `${termCount} term${termCount !== 1 ? "s" : ""}`, error && "1 error msg"].filter(Boolean).join(", ") || "None";

  return (
    <Create title="Create Coupon">
      <>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <WorkspaceContainer>

          {/* ── Header ── */}
          <WorkspaceHeader>
            <SectionTitleRow>
              <WorkspaceIconBadge>
                <LocalOfferOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </WorkspaceIconBadge>
              <div>
                <WorkspaceTitle>New Coupon</WorkspaceTitle>
                <WorkspaceSubtitle>Fill in the details to create a new discount coupon</WorkspaceSubtitle>
              </div>
            </SectionTitleRow>

            <WorkspaceHeaderMeta>
              <HeaderMetaGroup>
                <HeaderMetaLabel>Active</HeaderMetaLabel>
                <div style={{ cursor: "pointer" }} onClick={() => setIsActive(!isActive)}>
                  <StatusToggleSwitch active={isActive}><span className="slider" /></StatusToggleSwitch>
                </div>
              </HeaderMetaGroup>
              <HeaderMetaGroup>
                <HeaderMetaLabel>Visible on App</HeaderMetaLabel>
                <div style={{ cursor: "pointer" }} onClick={() => setVisibleOnApp(!visibleOnApp)}>
                  <StatusToggleSwitch active={visibleOnApp}><span className="slider" /></StatusToggleSwitch>
                </div>
              </HeaderMetaGroup>
            </WorkspaceHeaderMeta>
          </WorkspaceHeader>

          {/* ── Identity ── */}
          <WorkspacePanel>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Coupon Identity</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <Box display="flex" gap={1.5} alignItems="flex-end">
              <FormFieldGroup style={{ flex: 1 }}>
                <FormLabel>Coupon Code</FormLabel>
                <TextField
                  value={couponCode}
                  disabled={disabled}
                  fullWidth
                  size="small"
                  placeholder="e.g. SUMMER20"
                  inputProps={{ style: { fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.08em" } }}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <Button
                variant={disabled ? "contained" : "outlined"}
                size="small"
                color="inherit"
                onClick={() => setDisabled(!disabled)}
                sx={{ whiteSpace: "nowrap", mb: "1px", flexShrink: 0, textTransform: "none", height: "40px" }}
              >
                {disabled ? "Enter manually" : "Auto-generate"}
              </Button>
            </Box>

            <ThreeColGrid>
              <FormFieldGroup>
                <FormLabel>Coupon Value</FormLabel>
                <TextField
                  value={couponValue}
                  type="number"
                  fullWidth
                  size="small"
                  placeholder="0"
                  onChange={(e) => setCouponValue(e.target.value)}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Coupon Type</FormLabel>
                <Select fullWidth value={couponType} displayEmpty onChange={(e) => setCouponType(e.target.value)} sx={selectSx}>
                  <MenuItem value="" disabled><em style={{ color: "#999" }}>Select type</em></MenuItem>
                  {Object.values(CouponType).map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </Select>
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Product Type</FormLabel>
                <Select fullWidth value={productType} displayEmpty onChange={(e) => setProductType(e.target.value)} sx={selectSx}>
                  <MenuItem value="" disabled><em style={{ color: "#999" }}>Select type</em></MenuItem>
                  {Object.values(ProductType).map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </Select>
              </FormFieldGroup>
            </ThreeColGrid>
          </WorkspacePanel>

          {/* ── Scheduling ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Scheduling</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <FormGrid>
              <FormFieldGroup>
                <FormLabel>Start Date</FormLabel>
                <DateTimePicker
                  inputFormat="dd MMM yyyy HH:mm"
                  value={startDate}
                  disableMaskedInput
                  onChange={(v) => setStartDate(v)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" sx={fieldSx} />}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Expiry Date</FormLabel>
                <DateTimePicker
                  inputFormat="dd MMM yyyy HH:mm"
                  value={expiryDate}
                  disableMaskedInput
                  onChange={(v) => setExpiryDate(v)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" sx={fieldSx} />}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Reason Category</FormLabel>
                <Select fullWidth value={selectedCategory} displayEmpty onChange={(e) => setSelectedCategory(e.target.value)} sx={selectSx}>
                  <MenuItem value="" disabled><em style={{ color: "#999" }}>Select category</em></MenuItem>
                  {categoryItems.map((item) => <MenuItem key={item.category} value={item.category}>{item.category}</MenuItem>)}
                </Select>
              </FormFieldGroup>
              {selectedCategory && (
                <FormFieldGroup>
                  <FormLabel>Reason Sub-Category</FormLabel>
                  <Select fullWidth value={reasonSubCategory} displayEmpty onChange={(e) => setReasonSubCategory(e.target.value)} sx={selectSx}>
                    <MenuItem value="" disabled><em style={{ color: "#999" }}>Select sub-category</em></MenuItem>
                    {categoryItems.find((c) => c.category === selectedCategory)?.subCategory.map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </Select>
                </FormFieldGroup>
              )}
            </FormGrid>
          </WorkspacePanel>

          {/* ── Limits ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Limits</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <ThreeColGrid>
              <FormFieldGroup>
                <FormLabel>Min Order Value</FormLabel>
                <TextField
                  value={minimumOrderValue}
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  onChange={(e) => setMinimumOrderValue(e.target.value)}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Max Discount</FormLabel>
                <TextField
                  value={maximumDiscountAmount}
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  onChange={(e) => setMaximumDiscountAmount(e.target.value)}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Min Total After Discount</FormLabel>
                <TextField
                  value={minimumTotalAfterDiscount}
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  onChange={(e) => setMinimumTotalAfterDiscount(e.target.value)}
                  sx={fieldSx}
                />
              </FormFieldGroup>
            </ThreeColGrid>
            <FormGrid style={{ marginTop: 16 }}>
              <FormFieldGroup>
                <FormLabel>Usage Limit / Customer</FormLabel>
                <TextField
                  value={usageLimitPerCustomer}
                  type="number"
                  fullWidth
                  size="small"
                  onChange={(e) => setUsageLimitPerCustomer(e.target.value as any)}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Max Total Usage</FormLabel>
                <TextField
                  value={maximumUsageLimit}
                  type="number"
                  fullWidth
                  size="small"
                  onChange={(e) => setMaximumUsageLimit(e.target.value as any)}
                  sx={fieldSx}
                />
              </FormFieldGroup>
            </FormGrid>
          </WorkspacePanel>

          {/* ── Display ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Display</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <FormFieldGroup>
              <FormLabel>Coupon Header</FormLabel>
              <TextField
                value={couponHeader}
                fullWidth
                size="small"
                placeholder="Short headline shown to users"
                onChange={(e) => setCouponHeader(e.target.value)}
                sx={fieldSx}
              />
            </FormFieldGroup>
            <Box mt={2}>
              <FormLabel>Description</FormLabel>
              <TextField
                value={couponDescription}
                multiline
                minRows={3}
                fullWidth
                size="small"
                placeholder="Detailed description of the coupon"
                onChange={(e) => setCouponDescription(e.target.value)}
                sx={{ ...fieldSx, mt: "2px", "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
              />
            </Box>
            <Box mt={2}>
              <FormLabel>Display String</FormLabel>
              <TextField
                value={couponDisplayString}
                multiline
                minRows={2}
                fullWidth
                size="small"
                placeholder="Optional custom display text"
                onChange={(e) => setCouponDisplayString(e.target.value)}
                sx={{ ...fieldSx, mt: "2px", "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
              />
            </Box>
          </WorkspacePanel>

          {/* ── Optional sections (accordions) ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection style={{ marginBottom: 12 }}>
              <WorkSpaceTitleRow>
                <SectionTitle>Optional Configuration</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <Box display="flex" flexDirection="column" gap={1}>

              {/* Targeting */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: targetingLabel !== "Not set" ? "success.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">User & Area Targeting</Typography>
                  </Box>
                  <Typography variant="caption" color={targetingLabel === "Not set" ? "text.disabled" : "primary.main"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {targetingLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <FormGrid>
                    <FormFieldGroup>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <FormLabel>User Set</FormLabel>
                        <Button size="small" color={disableButton ? "success" : "inherit"} variant="text" sx={{ fontSize: "0.7rem", py: 0, minWidth: 0, textTransform: "none" }} onClick={() => setDisableButton(!disableButton)}>
                          {disableButton ? "Enable" : "Disable"}
                        </Button>
                      </Box>
                      <TextField
                        placeholder="One user ID per line"
                        value={userSet || ""}
                        disabled={disableButton}
                        multiline
                        minRows={4}
                        maxRows={8}
                        fullWidth
                        size="small"
                        helperText={userCount > 0 ? `${userCount} valid IDs` : "One ID per line"}
                        onChange={(e) => setUserSet(e.target.value)}
                        sx={{ ...fieldSx, "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                    <FormFieldGroup>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <FormLabel>Area Set</FormLabel>
                        <Button size="small" color={disableAreaSetButton ? "success" : "inherit"} variant="text" sx={{ fontSize: "0.7rem", py: 0, minWidth: 0, textTransform: "none" }} onClick={() => setDisableAreaSetButton(!disableAreaSetButton)}>
                          {disableAreaSetButton ? "Enable" : "Disable"}
                        </Button>
                      </Box>
                      <TextField
                        placeholder="One area ID per line"
                        value={areaSet || ""}
                        disabled={disableAreaSetButton}
                        multiline
                        minRows={4}
                        maxRows={8}
                        fullWidth
                        size="small"
                        helperText={areaCount > 0 ? `${areaCount} valid IDs` : "One ID per line"}
                        onChange={(e) => setAreaSet(e.target.value)}
                        sx={{ ...fieldSx, "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                  </FormGrid>
                </AccordionDetails>
              </Accordion>

              {/* Rules */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: rulesLabel !== "None" ? "success.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">Inclusion / Exclusion Rules</Typography>
                  </Box>
                  <Typography variant="caption" color={rulesLabel === "None" ? "text.disabled" : "success.main"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {rulesLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <Box display="flex" flexDirection="column" gap={2.5}>

                    {/* Date Rule */}
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <FormLabel>Date Rule</FormLabel>
                        <Button size="small" variant={inclusiveData ? "contained" : "outlined"} color="primary" sx={{ fontSize: "0.7rem", py: 0.25, textTransform: "none" }} onClick={() => setDateInclusiveExclusive(true)}>
                          {inclusiveData ? "Edit Rule" : "+ Add"}
                        </Button>
                      </Box>
                      {inclusiveData ? (
                        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                          <Box display="flex" gap={0.75} flexWrap="wrap" mb={0.75}>
                            <Chip label={inclusiveData.type} size="small" color={inclusiveData.type === "inclusion" ? "success" : "error"} sx={{ fontSize: "0.68rem", height: 20, fontWeight: 700 }} />
                            <Chip label={inclusiveData.period} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 20 }} />
                          </Box>
                          {inclusiveData.period === "DAYS_OF_WEEK"
                            ? <Box display="flex" flexWrap="wrap" gap={0.5}>{inclusiveData.values?.map((v: string) => <Chip key={v} label={v} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 20 }} />)}</Box>
                            : <Table size="small">
                                <TableHead><TableRow>
                                  <TableCell sx={{ py: 0.5, fontSize: "0.7rem", fontWeight: 700 }}>Start</TableCell>
                                  <TableCell sx={{ py: 0.5, fontSize: "0.7rem", fontWeight: 700 }}>End</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                  {inclusiveData.values?.map((each: any, i: number) => (
                                    <TableRow key={i}>
                                      <TableCell sx={{ py: 0.5, fontSize: "0.7rem" }}>{each.startDate ? formatDate(each.startDate) : "—"}</TableCell>
                                      <TableCell sx={{ py: 0.5, fontSize: "0.7rem" }}>{each.endDate ? formatDate(each.endDate) : "—"}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                          }
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No date rule configured</Typography>
                      )}
                    </Box>

                    <HorizontalLine />

                    {/* User Rule */}
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <FormLabel>User Rule</FormLabel>
                        <FormControlLabel
                          sx={{ m: 0, gap: 0.5 }}
                          control={<Switch size="small" checked={disableUserInclusionExclusion} onChange={(e) => setDisableUserInclusionExclusion(e.target.checked)} />}
                          label={<Typography variant="caption" color="text.secondary">Enable</Typography>}
                        />
                      </Box>
                      {disableUserInclusionExclusion ? (
                        <Box display="flex" gap={1.5} alignItems="center">
                          <Select size="small" value={String(userInclusionExclusion)} sx={{ ...selectSx, minWidth: 130 }}
                            onChange={(e) => {
                              const isInclusion = e.target.value === "true";
                              setUserInclusionExclusion(isInclusion);
                              setUserInclusionExclusionData({ type: isInclusion ? "inclusion" : "exclusion", rule: "FIRST_TIME_USER" });
                            }}
                          >
                            <MenuItem value="true">Inclusion</MenuItem>
                            <MenuItem value="false">Exclusion</MenuItem>
                          </Select>
                          <Chip label="FIRST_TIME_USER" size="small" color="primary" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No user rule configured</Typography>
                      )}
                    </Box>

                    <HorizontalLine />

                    {/* Time Rule */}
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <FormLabel>Time Rule</FormLabel>
                        <FormControlLabel
                          sx={{ m: 0, gap: 0.5 }}
                          control={<Switch size="small" checked={timeInclusionExclusion} onChange={(e) => setTimeInclusionExclusion(e.target.checked)} />}
                          label={<Typography variant="caption" color="text.secondary">Enable</Typography>}
                        />
                      </Box>
                      {timeInclusionExclusion ? (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          <FormFieldGroup>
                            <FormLabel>Type</FormLabel>
                            <Select size="small" value={String(addTimeInclusion)} sx={{ ...selectSx, maxWidth: 180 }} onChange={(e) => setAddTimeInclusion(e.target.value === "true")}>
                              <MenuItem value="true">Inclusion</MenuItem>
                              <MenuItem value="false">Exclusion</MenuItem>
                            </Select>
                          </FormFieldGroup>
                          <FormFieldGroup>
                            <FormLabel>Time Slots</FormLabel>
                            <Select
                              multiple fullWidth size="small"
                              value={selectedTimeEnums}
                              sx={selectSx}
                              onChange={(e) => setSelectedTimeEnums(e.target.value)}
                              renderValue={(selected: TimeEnum[]) => (
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  {selected.map((v) => <Chip key={v} label={v} size="small" color="primary" sx={{ height: 20, fontSize: "0.68rem" }} />)}
                                </Box>
                              )}
                            >
                              {Object.entries(TimeEnum).map(([key, value]) => <MenuItem key={key} value={value}>{value}</MenuItem>)}
                            </Select>
                          </FormFieldGroup>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No time rule configured</Typography>
                      )}
                    </Box>

                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Cashback */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: isCashbackEnabled ? "success.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">Cashback</Typography>
                  </Box>
                  <Typography variant="caption" color={isCashbackEnabled ? "success.main" : "text.disabled"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {cashbackLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <FormControlLabel
                    control={<Switch checked={isCashbackEnabled} onChange={(e) => setIsCashbackEnabled(e.target.checked)} color="success" />}
                    label={<Typography variant="body2" fontWeight={500} fontSize="14px">Enable Cashback</Typography>}
                  />
                  {isCashbackEnabled && (
                    <ThreeColGrid>
                      <FormFieldGroup>
                        <FormLabel>Cashback Type</FormLabel>
                        <Select fullWidth value={cashbackType} displayEmpty onChange={(e) => setCashbackType(e.target.value)} sx={selectSx}>
                          <MenuItem value="" disabled><em style={{ color: "#999" }}>Select</em></MenuItem>
                          <MenuItem value="FLAT">Flat (₹)</MenuItem>
                          <MenuItem value="PERCENT">Percent (%)</MenuItem>
                        </Select>
                      </FormFieldGroup>
                      <FormFieldGroup>
                        <FormLabel>Cashback Value</FormLabel>
                        <TextField value={cashbackValue} type="number" fullWidth size="small" onChange={(e) => setCashbackValue(e.target.value)} sx={fieldSx} />
                      </FormFieldGroup>
                      <FormFieldGroup>
                        <FormLabel>Expiry Days</FormLabel>
                        <TextField
                          value={cashbackExpiryDays}
                          type="number"
                          fullWidth
                          size="small"
                          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                          onChange={(e) => setCashbackExpiryDays(e.target.value)}
                          sx={fieldSx}
                        />
                      </FormFieldGroup>
                    </ThreeColGrid>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Terms & Errors */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: termCount > 0 ? "primary.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">Terms & Error Messages</Typography>
                  </Box>
                  <Typography variant="caption" color={termCount > 0 ? "primary.main" : "text.disabled"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {termsLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <FormGrid>
                    <FormFieldGroup>
                      <FormLabel>Terms & Conditions</FormLabel>
                      <TextField
                        placeholder="Term 1, Term 2, Term 3..."
                        value={termsAndConditions}
                        multiline
                        minRows={4}
                        fullWidth
                        size="small"
                        helperText={`Comma-separated · ${termCount} term${termCount !== 1 ? "s" : ""}`}
                        onChange={(e) => setTermsAndConditions(e.target.value)}
                        sx={{ ...fieldSx, mt: "2px", "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                    <FormFieldGroup>
                      <FormLabel>Error Message</FormLabel>
                      <TextField
                        value={error}
                        placeholder="Shown when coupon cannot be applied"
                        multiline
                        minRows={4}
                        fullWidth
                        size="small"
                        onChange={(e) => setError(e.target.value)}
                        sx={{ ...fieldSx, mt: "2px", "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                  </FormGrid>
                </AccordionDetails>
              </Accordion>

            </Box>
          </WorkspacePanel>

          {/* ── Action bar ── */}
          <Box
            sx={{
              mt: 3,
              px: 3,
              py: 2,
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: "0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
              <Chip label={isActive ? "Active" : "Inactive"} color={isActive ? "success" : "default"} size="small" sx={{ fontSize: "0.7rem" }} />
              <Chip label={visibleOnApp ? "Visible on App" : "Hidden"} color={visibleOnApp ? "info" : "default"} size="small" sx={{ fontSize: "0.7rem" }} />
              {couponCode && <Chip label={couponCode} color="primary" variant="outlined" size="small" sx={{ fontSize: "0.7rem", fontFamily: "monospace", fontWeight: 700 }} />}
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              sx={{ px: 4, fontWeight: 500, fontSize: "14px", textTransform: "none", backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#1565c0" } }}
            >
              Create Coupon
            </Button>
          </Box>

        </WorkspaceContainer>
        </Box>

        {/* ── Live customer-app preview ── */}
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            position: "sticky",
            top: 16,
            alignSelf: "flex-start",
            display: { xs: "none", lg: "block" },
            p: 2,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CouponAppPreview
            couponCode={couponCode}
            couponType={couponType}
            couponValue={couponValue}
            couponDescription={couponDescription}
            minimumOrderValue={minimumOrderValue}
            termsAndConditions={termsAndConditions ? termsAndConditions.split(",").map((t) => t.trim()).filter(Boolean) : []}
            errorMessages={error}
          />
        </Box>
        </Box>

        <AddDateInclusiveExclusiveInCoupon
          data={setInclusiveData}
          open={dateInclusiveExclusive}
          callback={() => setDateInclusiveExclusive(false)}
          setInclusiveData={setInclusiveData}
        />
      </>
    </Create>
  );
};

export default CouponCreateV2;
